// How a customer gets a product (a system's `provided_through`, declared in the customer
// context): 'direct' -- they contract with its vendor -- or the id of the vendor it comes
// through, resold or embedded, with no direct contract with the product's own vendor.
// A resold product expects no direct connection, so nothing calls it "Not Connected".
// Pure functions (no React), so tests run them under node.

export const DIRECT = 'direct'

// The vendors offered first as the channel a product comes through.
const FIRST_PROVIDERS = ['ukg']

export function isResold(system) {
  const p = String(system?.provided_through || DIRECT).trim().toLowerCase()
  return p !== '' && p !== DIRECT
}

// A vendor id's display name, from the platform-config vendor list; an unknown id reads as itself.
export function providerLabel(vendorId, vendors = []) {
  const id = String(vendorId || '').trim().toLowerCase()
  const v = (vendors || []).find(x => String(x.id || '').toLowerCase() === id)
  return v?.label || id.toUpperCase()
}

function nameOf(item, vendors) {
  return item?.provided_through_name || providerLabel(item?.provided_through, vendors)
}

// "Provided through UKG" for the customer; staff also read that there is no direct contract.
export function providedThroughText(item, vendors = [], { customer = false } = {}) {
  if (!isResold(item)) return ''
  const name = nameOf(item, vendors)
  return customer ? `Provided through ${name}` : `Provided through ${name} (no direct contract)`
}

// The API Connectors card's note beside the product's domain and functions.
export function connectorNote(system, vendors = []) {
  return isResold(system) ? `provided through ${nameOf(system, vendors)} (no direct contract)` : ''
}

// The card's connection badge: { label, tone } -- tone is connected | error | via | none.
export function connectionBadge(system, { connectedCount = 0, hasConnections = false } = {}, vendors = []) {
  if (connectedCount > 0) return { label: `${connectedCount} Connection${connectedCount > 1 ? 's' : ''}`, tone: 'connected' }
  if (hasConnections) return { label: 'Connection Error', tone: 'error' }
  if (isResold(system)) return { label: `Via ${nameOf(system, vendors)}`, tone: 'via' }
  return { label: 'Not Connected', tone: 'none' }
}

// The edit form's "Contracted through" choices: Direct, then the usual providers, then
// every other vendor -- never the product's own vendor (through itself is direct).
export function contractedThroughOptions(vendors = [], ownVendorId = '') {
  const own = String(ownVendorId || '').trim().toLowerCase()
  const rest = (vendors || []).filter(v => v?.id && String(v.id).toLowerCase() !== own)
  const first = FIRST_PROVIDERS.map(id => rest.find(v => String(v.id).toLowerCase() === id)).filter(Boolean)
  const others = rest.filter(v => !first.includes(v))
  return [{ id: DIRECT, label: 'Direct' }, ...first.map(v => ({ id: v.id, label: v.label })),
    ...others.map(v => ({ id: v.id, label: v.label }))]
}
